import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";

export const InputField = ({
  label,
  placeholder,
  name,
  value,
  onChange,
  type = "text",
  required,
  errors,
  disabled,
}) => (
  <div className="w-full">
    {label && (
      <label className="block mb-2 text-lg text-info dark:text-grey font-medium">
        {label}
      </label>
    )}
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      className={`block w-full p-3 bg-transparent border rounded-lg
        ${
          disabled
            ? "text-gray-500 border-info dark:border-gray-300 cursor-not-allowed"
            : "text-dark dark:text-gray-100 border-info dark:border-grey"
        }`}
      placeholder={placeholder}
      required={required}
      disabled={disabled}
      autoComplete="off"
    />
    {errors && <small className="text-red-500">{errors}</small>}
  </div>
);

export const NumberField = ({
  label,
  name,
  value,
  onChange,
  type = "number",
  required = true,
  errors,
}) => (
  <div className="flex gap-2">
    <label className="block text-base text-info dark:text-grey font-medium text-nowrap">
      {label}
    </label>
    <div className="flex-1">
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        className="w-full text-base text-dark dark:text-gray-100 focus:outline-none bg-transparent border-0 border-b border-info dark:border-gray-300 dark:border-gray-600 focus:border-b-2 focus:border-info dark:focus:border-gray-300 focus:ring-0 hover:border-b-2 hover:border-gray-500 peer"
        required={required}
        autoComplete="off"
      />
      {errors && <small className="text-red-500">{errors}</small>}
    </div>
  </div>
);

export const NumberFieldBorder = ({
  label,
  placeholder,
  name,
  value,
  onChange,
  type = "number",
  required,
  errors,
  disabled = false,
}) => (
  <div className="w-full">
    <label className="block mb-2 text-lg text-info dark:text-grey font-medium">
      {label}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      className={`block w-full p-3 bg-transparent border rounded-lg focus:ring-blue-500 focus:border-blue-500
        ${
          disabled
            ? "text-gray-500 border-gray-300"
            : "text-info dark:text-gray-100 border-info dark:border-gray-300"
        }`}
      placeholder={placeholder}
      required={required}
      autoComplete="off"
    />
    {errors && <small className="text-red-500">{errors}</small>}
  </div>
);

export const InputFieldWithOutBorder = ({
  label,
  name,
  value,
  onChange,
  type = "text",
  required = true,
  errors,
}) => (
  <div className="flex gap-2">
    <label className="block text-base font-medium text-info dark:text-grey font-medium text-nowrap">
      {label}
    </label>
    <div className="flex-1">
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        className="w-full text-base text-dark dark:text-gray-100 focus:outline-none bg-transparent border-0 border-b border-info dark:border-gray-300 dark:border-gray-600 focus:border-b-2 focus:border-info dark:focus:border-gray-300 focus:ring-0 hover:border-b-2 hover:border-gray-500 peer"
        required={required}
        autoComplete="off"
      />
      {errors && <small className="text-red-500">{errors}</small>}
    </div>
  </div>
);

export const RadioGroup = ({ label, name, value, onChange, options = [] }) => {
  return (
    <div className="mb-4 text-info dark:text-grey">
      <label className="block mb-2 text-lg font-medium">{label}</label>
      <div className="flex items-center gap-4">
        {options.map((option) => (
          <label
            key={option.value}
            className="flex items-center cursor-pointer"
          >
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={value === option.value}
              onChange={() => onChange(option.value)}
              className="mr-2 cursor-pointer"
            />
            {option.label}
          </label>
        ))}
      </div>
    </div>
  );
};

export const AuthInputField = ({
  type,
  value,
  onChange,
  placeholder,
  error,
  onKeyDown,
}) => (
  <div className="mb-5 relative">
    <input
      type={type}
      value={value}
      onChange={onChange}
      onKeyDown={onKeyDown}
      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
      placeholder={placeholder}
      required
    />
    {error && <small className="text-red-500 text-sm mt-1">{error}</small>}
  </div>
);

export const FormInput = ({
  label,
  name,
  type = "text",
  value,
  onChange,
  error,
  disabled = false,
}) => {
  return (
    <div className="flex flex-col">
      <div className="col-span-1 flex items-center gap-5 mb-4 ">
        <label
          htmlFor={name}
          className="text-sm text-info dark:text-grey font-medium w-1/4"
        >
          {label}
        </label>
        <input
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          disabled={disabled}
          autoComplete="off"
          className={`
            block w-full text-sm text-dark dark:text-gray-100
            bg-transparent border-0 border-b border-info dark:border-gray-300
            focus:outline-none focus:border-info dark:focus:border-gray-300
             hover:border-gray-500
            disabled:text-gray-400 disabled:dark:text-gray-500 disabled:dark:border-gray-500 disabled:cursor-not-allowed disabled:border-info
            peer focus:ring-0
          `}
        />
      </div>
      {error && (
        <small className="text-red-500 text-center mt-[-10px]">{error}</small>
      )}
    </div>
  );
};

export const FormDatePicker = ({
  label,
  name,
  value,
  onChange,
  error,
  minDate,
  maxDate,
  disabled = false,
}) => {
  return (
    <div className="flex flex-col">
      <div className="col-span-1 flex items-center gap-5 mb-4">
        <label
          htmlFor={name}
          className="text-sm font-medium text-info dark:text-grey w-1/4"
        >
          {label}
        </label>
        <Calendar
          value={value}
          onChange={onChange}
          onHide={() => {
            const input = document.querySelector(".asset_date input");
            if (input) input.blur();
          }}
          dateFormat="dd/mm/yy"
          className="asset_date w-full"
          minDate={minDate}
          maxDate={maxDate}
          disabled={disabled}
        />
      </div>
      {error && (
        <small className="text-red-500 text-center mt-[-10px]">{error}</small>
      )}
    </div>
  );
};

export const FormDropdown = ({
  label,
  name,
  value,
  onChange,
  options,
  error,
  optionsValue,
  optionsLabel,
  containerClassName = "col-span-1 flex items-center gap-5 mb-4",
  labelClassName = "text-sm font-medium text-info dark:text-grey w-1/4",
  mainClassName = "flex flex-col",
  disabled = false,
  className,
  showClear = false,
  placeholder,
}) => {
  return (
    <div className={mainClassName}>
      <div
        className={`${containerClassName} ${
          disabled ? "cursor-not-allowed" : ""
        }`}
      >
        <label htmlFor={name} className={labelClassName}>
          {label}
        </label>
        <Dropdown
          value={value}
          name={name}
          showClear={showClear}
          onChange={onChange}
          options={options}
          optionLabel={optionsLabel}
          optionValue={optionsValue}
          disabled={disabled}
          placeholder={placeholder}
          className={`${className} truncate`}
        />
      </div>
      {error && (
        <small className="text-red-500 text-center mt-[-10px]">{error}</small>
      )}
    </div>
  );
};

// ToggleSwitch.jsx
export const ToggleSwitch = ({
  label,
  name,
  checked,
  onChange,
  disabled = false,
  className = "",
  labelClass = "",
}) => {
  return (
    <div className={`flex gap-5 items-center mb-4 ${className}`}>
      {label && (
        <label
          className={`text-sm font-medium text-info dark:text-grey capitalize w-1/4 ${labelClass}`}
        >
          {label}
        </label>
      )}
      <label className="inline-flex items-center cursor-pointer">
        <input
          type="checkbox"
          checked={checked}
          onChange={onChange}
          className="sr-only peer"
          disabled={disabled}
        />
        <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600 dark:peer-checked:bg-blue-600"></div>
      </label>
    </div>
  );
};

// FormActions.jsx
export const FormActions = ({
  loading = false,
  onSave,
  onCancel,
  actionType = "Create",
  disabled = false,
  className = "",
  submitType = "button",
}) => {
  return (
    <div className={`mt-4 flex gap-2 ${className}`}>
      <button
        disabled={loading || disabled}
        // onClick={onSave}
        type={submitType}
        className={`dark:text-black items-center gap-1 hover:text-white text-sm font-medium bg-[radial-gradient(circle_at_10%_10%,rgb(237,237,237),#9e9e9e,#9e9e9e,#9e9e9e)] rounded-md border border-lightPrimary text-lightPrimary dark:button-primary-gradient rounded px-4 py-2 transition-all ${
          loading || disabled ? "bg-gray-600 opacity-50 cursor-not-allowed" : ""
        }`}
      >
        {loading ? "Saving..." : actionType === "Create" ? "Save" : "Update"}
      </button>
      <button
        onClick={onCancel}
        className="bg-transparent border border-lightPrimary dark:border-white text-lightPrimary dark:text-white text-sm px-4 py-2 rounded hover:bg-gray-600 transition-all"
      >
        Cancel
      </button>
    </div>
  );
};

export const InputBorderBottomBorder = ({
  label,
  name,
  value,
  onChange,
  type = "text",
  required = true,
  fieldClassName = "w-full text-base text-info dark:text-gray-100 focus:outline-none bg-transparent border-0 border-b border-info dark:border-gray-300 dark:border-gray-600 focus:border-b-2 focus:border-info dark:focus:border-gray-300 focus:ring-0 hover:border-b-2 hover:border-gray-500 peer",
  containerClassName = "",
  labelClassName = "",
  errors,
  disabled = false,
  suffix = "",
}) => (
  <div>
    <div className={`${containerClassName}`}>
      <label
        className={`block font-medium text-info dark:text-grey text-nowrap ${labelClassName}`}
      >
        {label}
      </label>
      <div className="relative w-full">
        <input
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          className={`${fieldClassName} ${suffix ? "pr-5" : ""}`}
          required={required}
          disabled={disabled}
          autoComplete="off"
        />
        {suffix && (
          <span className="absolute right-1 top-1/2 -translate-y-1/2 dark:text-gray-400 text-dark text-xs pointer-events-none">
            {suffix}
          </span>
        )}
      </div>
    </div>
    {errors && (
      <div className="text-end">
        <small className="text-red-500 ">{errors}</small>
      </div>
    )}
  </div>
);

export const Pagination = ({ page, limit, total, onPageChange }) => {
  const maxPage = Math.ceil(total / limit) - 1;

  if (total <= limit) return null;

  return (
    <div
      className={`flex gap-3 items-center px-3 pt-10 text-primary ${
        page > 0 ? "justify-between" : "justify-end"
      }`}
    >
      {page > 0 && (
        <button
          onClick={() => onPageChange(page - 1)}
          className="flex items-center gap-1"
        >
          <FaArrowLeft />
          View Previous
        </button>
      )}
      {page < maxPage && (
        <button
          onClick={() => onPageChange(page + 1)}
          className="flex items-center gap-1"
        >
          View Next
          <FaArrowRight />
        </button>
      )}
    </div>
  );
};
